<script setup>
// Renderiza uma mensagem de PRODUTO do WhatsApp (encaminhada pelo AstraCall como
// texto formatado + imagem) como um CARD na timeline do agente — parecido com o
// card nativo do WhatsApp. Detecção/roteamento fica no Message.vue (isProductMessage).
import { computed } from 'vue';
import { useMessageContext } from '../provider.js';
import { ATTACHMENT_TYPES } from '../constants';
import BaseBubble from 'next/message/bubbles/Base.vue';

const { content, attachments } = useMessageContext();

// imagem do produto (1º anexo de imagem)
const image = computed(() => {
  const img = (attachments?.value || []).find(
    a => a.fileType === ATTACHMENT_TYPES.IMAGE
  );
  return img?.dataUrl || '';
});

// extrai os campos do texto: 🛍️ *titulo* / descrição / 💰 preço (promoção: X) / 🔗 link / _ID: ..._
const product = computed(() => {
  const raw = String(content?.value || '');
  const lines = raw.split('\n');

  const grab = re => (raw.match(re) || [])[1]?.trim() || '';
  const title = grab(/🛍️\s*\*([^*]+)\*/) || grab(/🛍️\s*(.+)/);
  const priceLine = grab(/💰\s*(.+)/);
  const link = grab(/🔗\s*(\S+)/);
  const productId = grab(/_ID:\s*([^_]+)_/);

  // descrição: linhas entre a do 🛍️ e a do 💰
  const startIdx = lines.findIndex(l => l.includes('🛍️'));
  const priceIdx = lines.findIndex(l => l.includes('💰'));
  let description = '';
  if (startIdx > -1 && priceIdx > startIdx) {
    description = lines
      .slice(startIdx + 1, priceIdx)
      .map(l => l.trim())
      .filter(Boolean)
      .join(' ');
  }

  // preço + promoção: "BRL 250.00 (promoção: BRL 249.00)"
  let price = priceLine;
  let promo = '';
  const m = priceLine.match(/^(.+?)\s*\(promoç[ãa]o:\s*(.+?)\)\s*$/i);
  if (m) {
    price = m[1].trim();
    promo = m[2].trim();
  }

  return { title, description, price, promo, link, productId };
});
</script>

<template>
  <BaseBubble
    class="!p-0 overflow-hidden !max-w-[280px] min-w-[240px]"
    data-bubble-name="product-card"
    hide-meta
  >
    <img
      v-if="image"
      :src="image"
      alt=""
      class="w-full object-cover max-h-[180px]"
    />
    <div class="flex flex-col gap-1 p-3">
      <div class="flex items-center gap-1.5 text-n-slate-11">
        <span class="i-ph-shopping-bag-bold size-3.5" />
        <span class="text-[0.6875rem] font-medium uppercase tracking-wide">
          Produto
        </span>
      </div>
      <div
        v-if="product.title"
        class="font-medium leading-tight text-n-slate-12"
      >
        {{ product.title }}
      </div>
      <p
        v-if="product.description"
        class="text-sm leading-snug text-n-slate-11"
      >
        {{ product.description }}
      </p>
      <div v-if="product.price" class="flex items-baseline gap-2 mt-0.5">
        <span
          class="text-base font-semibold text-n-teal-11"
          :class="product.promo ? '' : ''"
        >
          {{ product.promo || product.price }}
        </span>
        <span
          v-if="product.promo"
          class="text-sm line-through text-n-slate-10"
        >
          {{ product.price }}
        </span>
      </div>
      <a
        v-if="product.link"
        :href="product.link"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center justify-center h-8 mt-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
      >
        Ver produto
      </a>
      <div v-if="product.productId" class="mt-1 text-[0.625rem] text-n-slate-10">
        ID: {{ product.productId }}
      </div>
    </div>
  </BaseBubble>
</template>
